import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { DynamicFormGenerator } from './DynamicFormGenerator';
import yaml from 'js-yaml';
export function EnhancedFormFiller({ fields, values, signatures, currentRole, onChange, onSignature, onImageUpload }) {
    const [showUpload, setShowUpload] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('idle');
    const [uploadMessage, setUploadMessage] = useState('');
    const fileInputRef = useRef(null);
    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        setUploadStatus('processing');
        setUploadMessage('Processing file...');
        try {
            const content = await file.text();
            const fileName = file.name.toLowerCase();
            let parsedData = {};
            // Determine file type and parse accordingly
            if (fileName.endsWith('.csv')) {
                parsedData = parseCSV(content);
            }
            else if (fileName.endsWith('.yaml') || fileName.endsWith('.yml')) {
                parsedData = yaml.load(content);
            }
            else if (fileName.endsWith('.json')) {
                parsedData = JSON.parse(content);
            }
            else if (fileName.endsWith('.txt') || fileName.endsWith('.md')) {
                parsedData = parseKeyValue(content);
            }
            else {
                throw new Error('Unsupported file format. Please use CSV, YAML, JSON, TXT, or MD files.');
            }
            // Apply parsed data to form fields
            let appliedCount = 0;
            Object.entries(parsedData).forEach(([key, value]) => {
                // Find matching field
                const field = fields.find(f => f.variableName === key ||
                    f.label.toLowerCase() === key.toLowerCase() ||
                    f.variableName.replace(/_/g, ' ').toLowerCase() === key.toLowerCase());
                if (field) {
                    onChange(field.variableName, value);
                    appliedCount++;
                }
            });
            setUploadStatus('success');
            setUploadMessage(`Successfully imported ${appliedCount} field${appliedCount !== 1 ? 's' : ''} from ${file.name}`);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            // Hide upload section after 3 seconds
            setTimeout(() => {
                setShowUpload(false);
                setUploadStatus('idle');
            }, 3000);
        }
        catch (error) {
            console.error('Error uploading file:', error);
            setUploadStatus('error');
            setUploadMessage(`Error: ${error.message}`);
        }
    };
    const parseCSV = (content) => {
        const lines = content.trim().split('\n');
        const result = {};
        lines.forEach(line => {
            const [key, ...valueParts] = line.split(',');
            if (key && valueParts.length > 0) {
                const value = valueParts.join(',').trim();
                // Remove quotes if present
                result[key.trim()] = value.replace(/^["']|["']$/g, '');
            }
        });
        return result;
    };
    const parseKeyValue = (content) => {
        const lines = content.trim().split('\n');
        const result = {};
        lines.forEach(line => {
            // Support multiple separators: :, =, |
            const separators = [':', '=', '|'];
            for (const sep of separators) {
                if (line.includes(sep)) {
                    const [key, ...valueParts] = line.split(sep);
                    if (key && valueParts.length > 0) {
                        result[key.trim()] = valueParts.join(sep).trim();
                    }
                    break;
                }
            }
        });
        return result;
    };
    const generateTemplateFile = (format) => {
        let content = '';
        const timestamp = Date.now();
        if (format === 'csv') {
            content = fields.map(f => `${f.variableName},`).join('\n');
        }
        else if (format === 'yaml') {
            const yamlObj = {};
            fields.forEach(f => {
                yamlObj[f.variableName] = '';
            });
            content = yaml.dump(yamlObj);
        }
        else if (format === 'json') {
            const jsonObj = {};
            fields.forEach(f => {
                jsonObj[f.variableName] = '';
            });
            content = JSON.stringify(jsonObj, null, 2);
        }
        else if (format === 'txt') {
            content = fields.map(f => `${f.variableName}: `).join('\n');
        }
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `template-${timestamp}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "rounded-lg p-4", style: {
                    backgroundColor: 'var(--bg-elevated)',
                    border: '1px solid var(--border-color)'
                }, children: [_jsxs("div", { className: "flex items-center justify-between mb-3", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Upload, { className: "w-5 h-5", style: { color: '#D97706' } }), _jsx("h3", { className: "font-semibold", style: { color: 'var(--text-primary)' }, children: "Bulk Import" })] }), _jsxs(Button, { variant: "outline", size: "sm", onClick: () => setShowUpload(!showUpload), style: { borderColor: '#D97706', color: '#D97706' }, children: [showUpload ? 'Hide' : 'Show', " Upload"] })] }), showUpload && (_jsxs("div", { className: "space-y-4", children: [_jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "Upload a CSV, YAML, JSON, TXT, or MD file with field values to auto-fill the form" }), uploadStatus !== 'idle' && (_jsxs("div", { className: "p-3 rounded flex items-start gap-3", style: {
                                    backgroundColor: uploadStatus === 'success' ? '#10b98110' :
                                        uploadStatus === 'error' ? '#ef444410' :
                                            '#D9770610',
                                    border: `1px solid ${uploadStatus === 'success' ? '#10b981' :
                                        uploadStatus === 'error' ? '#ef4444' :
                                            '#D97706'}`
                                }, children: [uploadStatus === 'processing' && (_jsx("div", { className: "animate-spin rounded-full h-5 w-5 border-b-2", style: { borderColor: '#D97706' } })), uploadStatus === 'success' && _jsx(CheckCircle, { className: "w-5 h-5", style: { color: '#10b981' } }), uploadStatus === 'error' && _jsx(AlertCircle, { className: "w-5 h-5", style: { color: '#ef4444' } }), _jsx("p", { className: "text-sm flex-1", style: {
                                            color: uploadStatus === 'success' ? '#10b981' :
                                                uploadStatus === 'error' ? '#ef4444' :
                                                    '#D97706'
                                        }, children: uploadMessage })] })), _jsxs("div", { children: [_jsx("input", { ref: fileInputRef, type: "file", accept: ".csv,.yaml,.yml,.json,.txt,.md", onChange: handleFileUpload, style: { display: 'none' } }), _jsxs(Button, { onClick: () => fileInputRef.current?.click(), variant: "outline", className: "w-full", children: [_jsx(Upload, { className: "w-4 h-4 mr-2" }), "Choose File to Upload"] })] }), _jsxs("div", { className: "pt-3 border-t", style: { borderColor: 'var(--border-color)' }, children: [_jsx("p", { className: "text-sm mb-2", style: { color: 'var(--text-secondary)' }, children: "Download empty template:" }), _jsxs("div", { className: "flex gap-2", children: [_jsxs(Button, { variant: "ghost", size: "sm", onClick: () => generateTemplateFile('csv'), children: [_jsx(FileText, { className: "w-3 h-3 mr-1" }), "CSV"] }), _jsxs(Button, { variant: "ghost", size: "sm", onClick: () => generateTemplateFile('yaml'), children: [_jsx(FileText, { className: "w-3 h-3 mr-1" }), "YAML"] }), _jsxs(Button, { variant: "ghost", size: "sm", onClick: () => generateTemplateFile('json'), children: [_jsx(FileText, { className: "w-3 h-3 mr-1" }), "JSON"] }), _jsxs(Button, { variant: "ghost", size: "sm", onClick: () => generateTemplateFile('txt'), children: [_jsx(FileText, { className: "w-3 h-3 mr-1" }), "TXT"] })] })] }), _jsxs("div", { className: "rounded p-3 text-xs font-mono", style: { backgroundColor: 'var(--bg-base)' }, children: [_jsx("p", { className: "font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Example formats:" }), _jsxs("p", { style: { color: 'var(--text-secondary)' }, children: [_jsx("strong", { children: "CSV:" }), " company_name, COW Group", _jsx("br", {}), _jsx("strong", { children: "YAML:" }), " company_name: COW Group", _jsx("br", {}), _jsx("strong", { children: "JSON:" }), " ", `{"company_name": "COW Group"}`, _jsx("br", {}), _jsx("strong", { children: "TXT:" }), " company_name: COW Group"] })] })] }))] }), _jsx(DynamicFormGenerator, { fields: fields, values: values, signatures: signatures, currentRole: currentRole, onChange: onChange, onSignature: onSignature, onImageUpload: onImageUpload })] }));
}
